import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionArbitrageManagerAxiosApi, matchingOrchestratorAxiosApi } from 'src/Config/axiosApi';
import {
  PositionsGroupedByPair,
  PerformanceData,
  PerformanceAccount,
  PerformanceFailureReason,
  PredictionCashflow
} from '../types';

/**
 * The single marker for a value the backend could not read. Deliberately a
 * word: `0`, `-` and an empty cell all read as a real (and reassuring) number
 * to an operator scanning the bar.
 */
export const UNAVAILABLE_LABEL = 'unavailable';

/** Money, or the unavailable marker — never a zero standing in for a failed read. */
export function formatUsdOrUnavailable(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return UNAVAILABLE_LABEL;
  return '$' + value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * What the operator has to DO about a failed read. The two reasons stay apart
 * everywhere: one is ours (the secret never reached the pod), the other is the
 * venue's.
 */
export function failureReasonLabel(
  reason: PerformanceFailureReason | undefined
): string {
  if (reason === 'not_mounted') return 'credentials not mounted';
  if (reason === 'read_failed') return 'venue read failed';
  if (reason === 'unregistered') return 'credentials for an unregistered account';
  return 'reason unknown';
}

/**
 * The two sentinel account ids the backend uses when the failure is not about
 * one account. Printing them raw tells the operator nothing — and `<registry>`
 * in particular means the whole account LIST is a guess off the credentials
 * mount, which changes how every other number on the page should be read.
 */
export const NO_ACCOUNT_SENTINEL = '<none>';
export const REGISTRY_SENTINEL = '<registry>';

export function failedAccountLabel(failed: {
  accountId: string;
  platform: 'kalshi' | 'polymarket' | 'registry';
  reason?: PerformanceFailureReason;
  detail?: string;
}): string {
  if (failed.accountId === REGISTRY_SENTINEL || failed.platform === 'registry') {
    return 'venue_accounts could not be read — the account list was guessed off the credentials mount';
  }
  if (failed.accountId === NO_ACCOUNT_SENTINEL) {
    return `no account at all is registered for ${failed.platform}`;
  }
  return `${failed.accountId} (${failed.platform}): ${failureReasonLabel(failed.reason)}`;
}

/**
 * The performance slice for one account, scoped by platform because
 * `accountId` is unique PER PLATFORM.
 */
export function findPerformanceAccount(
  performance: PerformanceData | undefined,
  platform: 'kalshi' | 'polymarket',
  accountId: string
): PerformanceAccount | undefined {
  return performance?.accounts?.find(
    a => a.platform === platform && a.accountId === accountId
  );
}

export function performanceAccountsFor(
  performance: PerformanceData | undefined,
  platform: 'kalshi' | 'polymarket'
): PerformanceAccount[] {
  return (performance?.accounts ?? []).filter(a => a.platform === platform);
}

interface PositionStats {
  total: number;
  open: number;
  closed: number;
  expired: number;
  totalInvested: number;
  totalUnrealizedPnl: number;
}

interface PositionsFilters {
  status?: string;
  platform?: string;
}

export function usePositionsGrouped(filters?: PositionsFilters) {
  return useQuery(
    ['positions-grouped', filters],
    async () => {
      const { data } = await predictionArbitrageManagerAxiosApi.get<
        PositionsGroupedByPair[]
      >('/positions/grouped', { params: filters });
      const groups = data.filter(g => g._id != null);
      const unmatched = data.filter(g => g._id == null);
      return { groups, unmatched };
    },
    {
      keepPreviousData: true,
      // Open positions can close outside this view; poll so stale rows disappear automatically.
      refetchInterval: filters?.status === 'open' ? 10000 : false,
      refetchIntervalInBackground: filters?.status === 'open',
    }
  );
}

export function usePositionsEnded() {
  return useQuery(
    ['positions-ended'],
    async () => {
      const { data } = await predictionArbitrageManagerAxiosApi.get<
        PositionsGroupedByPair[]
      >('/positions/grouped', { params: { status: 'ended' } });
      return data;
    },
    { keepPreviousData: true }
  );
}

export function usePositionStats() {
  return useQuery(['position-stats'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PositionStats>(
        '/positions/stats'
      );
    return data;
  });
}

export function usePerformance() {
  return useQuery(['position-performance'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PerformanceData>(
        '/positions/performance'
      );
    return data;
  }, { refetchInterval: 30000 });
}

export function useCashflows() {
  return useQuery(['position-cashflows'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PredictionCashflow[]>(
        '/positions/cashflows'
      );
    return data;
  });
}

export function useCreateCashflow() {
  const qc = useQueryClient();
  return useMutation(
    async (body: { platform: string; type: string; amountUsd: number; note?: string; date?: string }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post('/positions/cashflows', body);
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('position-cashflows');
        qc.invalidateQueries('position-performance');
      }
    }
  );
}

type GroupedPositionsCache = {
  groups: PositionsGroupedByPair[];
  unmatched: PositionsGroupedByPair[];
};

/**
 * Per-position opt-in for TNA (time-to-no-arb) alerts. Default OFF; when OFF
 * the MTNA/TTNA cells render `-`. PATCHes the prediction-arbitrage-manager
 * endpoint and optimistically flips the flag on the matching grouped row so
 * the switch reflects the change immediately, rolling back on error.
 */
export function useToggleTnaAlerts() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, enabled }: { pairId: string; enabled: boolean }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.patch(
        `/positions/${pairId}/tna-alerts`,
        { enabled }
      );
      return data;
    },
    {
      mutationKey: 'positions:toggle-tna-alerts',
      onMutate: async ({ pairId, enabled }) => {
        await qc.cancelQueries('positions-grouped');
        const snapshot = qc.getQueriesData<GroupedPositionsCache>(
          'positions-grouped'
        );
        const flip = (g: PositionsGroupedByPair) =>
          g._id === pairId ? { ...g, tnaAlertsEnabled: enabled } : g;
        qc.setQueriesData<GroupedPositionsCache | undefined>(
          'positions-grouped',
          (prev) =>
            prev
              ? {
                  groups: prev.groups.map(flip),
                  unmatched: prev.unmatched.map(flip),
                }
              : prev
        );
        return { snapshot };
      },
      onError: (_err, _vars, context) => {
        const ctx = context as
          | { snapshot: [unknown, GroupedPositionsCache | undefined][] }
          | undefined;
        ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key as never, data));
      },
      onSettled: () => {
        qc.invalidateQueries('positions-grouped');
      },
    }
  );
}

export function useTogglePairArchive() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, archived }: { pairId: string; archived: boolean }) => {
      const { data } = await matchingOrchestratorAxiosApi.patch(
        `/pairs/${pairId}`,
        { archived }
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
      }
    }
  );
}

export function useUpdatePairExpiry() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, expiryDate }: { pairId: string; expiryDate: string }) => {
      const { data } = await matchingOrchestratorAxiosApi.patch(`/pairs/${pairId}`, {
        expiryDate,
      });
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
      }
    }
  );
}

/**
 * Manually close an open position. The exchange API may still report it
 * as open (e.g. you exited via the exchange UI but the API hasn't dropped
 * the row yet, or you want to hide a stuck row before the absence-counter
 * / endDate fallback fires). Hits POST /positions/:id/close which sets
 * `closedManually: true` so subsequent sync cycles skip the position and
 * cannot revert status back to 'open'.
 *
 * Does NOT trade. The user has already exited (or will exit) on the
 * exchange.
 */
export function useClosePositionManually() {
  const qc = useQueryClient();
  return useMutation(
    async (positionId: string) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post(
        `/positions/${positionId}/close`
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
        qc.invalidateQueries('position-stats');
      },
    }
  );
}

export function useSyncPositions() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.post('/positions/sync');
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
        qc.invalidateQueries('position-stats');
        qc.invalidateQueries('position-performance');
      }
    }
  );
}
